import { Suspense } from 'react'

import { useUserById } from 'src/shared/features/users/api/accounts/use-user-by-id'
import type { NotificationActivity, NotificationGroup } from 'src/shared/schemas/stream/notification'
import { cn } from 'src/shared/utils/cn'
import { formatMessageTime } from 'src/shared/utils/dates/format-message-time'

import { useMarkNotificationAsRead } from '../api/use-mark-notifications-as-read'
import useUserNotificationContent from '../hooks/use-notification-content'
import NotificationSkeleton from '../skeletons/notification-skeleton'

interface NotificationItemProps {
  activity: NotificationActivity
  group: NotificationGroup
}

const NotificationItemContent = ({ activity, group }: NotificationItemProps) => {
  const markAsRead = useMarkNotificationAsRead()
  const firstActorId = activity.actor.split(':')[1]
  const { data: firstUser } = useUserById(Number(firstActorId))

  const notification = useUserNotificationContent(activity, group, firstUser ?? null)

  const handleClick = () => {
    setTimeout(async () => {
      if (!group.isRead) {
        try {
          await markAsRead.mutateAsync(group.id)
        } catch (error) {
          console.error('Failed to mark notification as read:', error)
        }
      }
    }, 0)

    if (notification.onPress) {
      notification.onPress()
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'flex w-full items-stretch gap-4 border-t border-lightBorder bg-white px-4 py-3 text-left hover:bg-accent/50 focus:bg-accent/50',
        !group.isRead && 'bg-accent',
      )}
    >
      <div className="flex w-full items-center gap-4">
        <div className="flex flex-1 items-center gap-3">
          <div className="relative shrink-0">{notification.icon}</div>
          <div className="flex flex-1 flex-col items-start">
            {notification.title}
            {notification.subtitle}
          </div>
        </div>
        <div className="ml-2 flex items-center gap-2">
          {notification.rightContent}
          <div className="flex items-center">
            <p className="text-[11px] text-muted">{formatMessageTime(activity.time, { short: true })}</p>
          </div>
        </div>
      </div>
    </button>
  )
}

export const NotificationItem = ({ activity, group }: NotificationItemProps) => {
  if (!activity || !group) return null

  return (
    <Suspense fallback={<NotificationSkeleton />}>
      <NotificationItemContent activity={activity} group={group} />
    </Suspense>
  )
}

export default NotificationItem
